import { useState, type ReactNode } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PickerNoMatches,
  PickerSearchHeader,
  usePickerSearch,
} from "@/lib/picker-search";
import { thumbFallback, thumbUrl } from "@/lib/img-thumb";

export type PickerOption = {
  value: string;
  label: string;
  imageUrl?: string | null;
  description?: string | null;
};
export type PickerGroup = { label: string; options: PickerOption[] };

export function PickerSelect({
  icon, label, placeholder, value, onChange, options, groups, testId, disabled, searchable, wrapDescription,
}: {
  icon: ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options?: PickerOption[];
  groups?: PickerGroup[];
  testId: string;
  disabled?: boolean;
  searchable?: boolean;
  wrapDescription?: boolean;
}) {
  const filled = !!value && value !== "__none__";
  const allOptions = groups ? groups.flatMap((g) => g.options) : (options ?? []);
  const hasAvatars = allOptions.some((o) => !!o.imageUrl);
  const selected = filled ? allOptions.find((o) => o.value === value) : undefined;
  const selectedDescription = selected?.description?.trim() || "";
  const [open, setOpen] = useState(false);
  const { showSearch, query, setQuery, searchInputRef, isSearching, matchesLabel } =
    usePickerSearch(open, allOptions.length, !!searchable);
  const matchesQuery = (o: PickerOption) => matchesLabel(o.label);
  const filteredOptions = isSearching ? (options ?? []).filter(matchesQuery) : (options ?? []);
  const filteredGroups = (groups ?? [])
    .map((g) => ({ label: g.label, options: isSearching ? g.options.filter(matchesQuery) : g.options }))
    .filter((g) => g.options.length > 0);
  const hasAnyResult = groups ? filteredGroups.length > 0 : filteredOptions.length > 0;
  const renderPickerOption = (o: PickerOption) => {
    const desc = o.description?.trim() || "";
    return (
      <SelectItem key={o.value} value={o.value} className="text-xs">
        <div className={["flex gap-2 min-w-0", wrapDescription ? "items-start" : "items-center"].join(" ")}>
          {hasAvatars ? (
            o.imageUrl ? (
              <img
                src={thumbUrl(o.imageUrl, 64)!}
                onError={thumbFallback(o.imageUrl)}
                alt=""
                className="h-6 w-6 rounded-full object-cover shrink-0 border border-border/40"
                loading="lazy"
              />
            ) : (
              <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 border border-border/40 text-[10px] font-semibold text-muted-foreground">
                {o.label.trim().charAt(0).toUpperCase() || "?"}
              </div>
            )
          ) : null}
          <div className="flex flex-col min-w-0 flex-1">
            <span className="truncate">{o.label}</span>
            {desc && (
              <span
                className={[
                  "text-[11px] text-muted-foreground/80 leading-snug mt-0.5",
                  wrapDescription ? "whitespace-normal break-words" : "truncate",
                ].join(" ")}
                data-testid={`text-option-description-${o.value}`}
              >
                {desc}
              </span>
            )}
          </div>
        </div>
      </SelectItem>
    );
  };
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled} open={open} onOpenChange={setOpen}>
      <SelectTrigger
        className={[
          "group h-auto min-h-[72px] rounded-xl px-3 py-2.5 transition-all duration-150",
          "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          filled
            ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
            : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
        ].join(" ")}
        data-testid={testId}
      >
        <div className="flex items-center gap-3 w-full min-w-0">
          <div
            className={[
              "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
              filled
                ? "bg-primary/15 text-primary ring-primary/20"
                : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
            ].join(" ")}
          >
            {icon}
          </div>
          <div className="flex flex-col items-start min-w-0 flex-1 text-left">
            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
              {label}
              {!filled && <span className="text-muted-foreground/40">*</span>}
            </span>
            {filled && selected ? (
              <div className="flex flex-col w-full min-w-0">
                <span className="text-sm truncate w-full leading-tight text-foreground font-semibold">
                  {selected.label}
                </span>
                {selectedDescription && (
                  <span
                    className="text-[11px] text-muted-foreground/80 truncate w-full leading-tight mt-0.5"
                    title={wrapDescription ? selectedDescription : undefined}
                  >
                    {selectedDescription}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm truncate w-full leading-tight text-muted-foreground/60 font-normal">
                <SelectValue placeholder={placeholder} />
              </span>
            )}
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]">
        {showSearch && (
          <PickerSearchHeader
            inputRef={searchInputRef}
            query={query}
            onQueryChange={setQuery}
            testId={`input-search-${testId}`}
          />
        )}
        {showSearch && !hasAnyResult && <PickerNoMatches />}
        {groups
          ? filteredGroups.map((g) => (
              <SelectGroup key={g.label}>
                <SelectLabel className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70">
                  {g.label}
                </SelectLabel>
                {g.options.map(renderPickerOption)}
              </SelectGroup>
            ))
          : filteredOptions.map(renderPickerOption)}
      </SelectContent>
    </Select>
  );
}